import React, { createContext, useContext, useState, ReactNode } from "react";
import { FeatureRequest } from "../types/featureRequest";

/**
 * Type definition for the feature request context value
 * @interface FeatureRequestContextType
 * @property {FeatureRequest[]} features - Array of feature requests
 * @property {function} setFeatures - Function to set the features array
 * @property {Set<string>} userVotes - Set of feature IDs the user has voted for
 * @property {function} setUserVotes - Function to set user votes
 * @property {boolean} isLoading - Loading state for feature requests
 * @property {function} setIsLoading - Function to set loading state
 * @property {string | null} error - Error message if any operation fails
 * @property {function} setError - Function to set error state
 * @property {boolean} isSubmitting - Whether a feature request is being submitted
 * @property {function} setIsSubmitting - Function to set submission state
 */
interface FeatureRequestContextType {
  features: FeatureRequest[];
  setFeatures: (features: FeatureRequest[]) => void;
  userVotes: Set<string>;
  setUserVotes: (votes: Set<string>) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  isSubmitting: boolean;
  setIsSubmitting: (submitting: boolean) => void;
}

// Create context with undefined default value
const FeatureRequestContext = createContext<
  FeatureRequestContextType | undefined
>(undefined);

/**
 * Props for the FeatureRequestProvider component
 * @interface FeatureRequestProviderProps
 * @property {ReactNode} children - Child components to be wrapped by the provider
 */
interface FeatureRequestProviderProps {
  children: ReactNode;
}

/**
 * FeatureRequestProvider component
 * Provides feature request state and management functions to child components
 * Manages features array, user votes, loading states, and error handling
 *
 * @component
 * @param {FeatureRequestProviderProps} props - Component props
 * @returns {React.ReactElement} Provider component
 */
export const FeatureRequestProvider: React.FC<FeatureRequestProviderProps> = ({
  children,
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ============================================================================
  // ============================================================================
  return (
    <FeatureRequestContext.Provider
      value={{
        features,
        setFeatures,
        userVotes,
        setUserVotes,
        isLoading,
        setIsLoading,
        error,
        setError,
        isSubmitting,
        setIsSubmitting,
      }}
    >
      {children}
    </FeatureRequestContext.Provider>
  );
};

/**
 * Custom hook to access the feature request context
 * @throws {Error} If used outside of FeatureRequestProvider
 * @returns {FeatureRequestContextType} Feature request context value
 */
export const useFeatureRequestContext = () => {
  const context = useContext(FeatureRequestContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureRequestContext must be used within a FeatureRequestProvider"
    );
  }
  return context;
};
